import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { RktButtonDirective } from '@rocketfinancialcorp/rocket-ui/button';
import { RktIconComponent } from '@rocketfinancialcorp/rocket-ui/icon';
import {
  DatatableColumn,
  DatatableSorting,
  FetchData,
  RktTableComponent,
  RktTableSearchComponent,
} from '@rocketfinancialcorp/rocket-ui/table';
import { FilterMenuComponent } from '@shared/components/filters';
import { FilterField, RequestPageParams, FilterValues } from '@shared/models';
import { financialAccountFeature, FinancialAcccountActions } from '@store';
import { identity, isUndefined, pickBy } from 'lodash-es';

@Component({
  selector: 'app-financial-account-journal-activity',
  imports: [CommonModule, RktIconComponent, RktButtonDirective, FilterMenuComponent, RktTableSearchComponent, RktTableComponent],
  templateUrl: './financial-account-journal-activity.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FinancialAccountJournalActivityComponent {
  store = inject(Store);
  router = inject(Router);
  activatedRoute = inject(ActivatedRoute);

  columns: DatatableColumn[] = [
    { name: 'Created At', prop: 'createdAt', fixedSize: 150, colType: 'date-time' },
    { name: 'Txn ID ', prop: 'transactionId', fixedSize: 150, colType: 'short-id' },
    { name: 'Description', prop: 'description', flexSize: 450 },
    { name: 'Entry Type', prop: 'entryType', flexSize: 200, colAlign: 'center' },
    { name: 'Amount', prop: 'amount', flexSize: 200, colType: 'amount', colAlign: 'right' },
    { name: 'Status', prop: 'entryState', fixedSize: 140, colType: 'status-tag', colAlign: 'right' },
  ];

  filters: FilterField[] = [
    {
      name: 'transactionId',
      displayName: 'Transaction Id',
      type: 'INPUT',
    },
    {
      name: 'date',
      displayName: 'Date',
      type: 'DATE',
      options: [
        { label: 'Today', value: 'TODAY' },
        { label: 'Yesterday', value: 'YESTERDAY' },
        { label: 'This week', value: 'THIS_WEEK' },
        { label: 'This month', value: 'THIS_MONTH' },
        { label: '90 days', value: 'DEFAULT' },
        { label: 'Custom range', value: 'CUSTOM' },
      ],
      defaultValue: 'DEFAULT',
      props: {
        maxMonths: '15',
      },
    },
    {
      name: 'entryType',
      displayName: 'Amount Type',
      type: 'RADIO',
      options: [
        { label: 'Credits', value: 'CREDIT' },
        { label: 'Debits', value: 'DEBIT' },
        { label: 'OPENING BALANCE', value: 'OPENING_BALANCE' },
      ],
    },
    {
      name: 'amount',
      displayName: 'Amount',
      type: 'AMOUNT',
    },
    {
      name: 'entryState',
      displayName: 'Status',
      type: 'RADIO',
      options: [
        { label: 'All', value: undefined },
        { label: 'Approved', value: 'APPROVED' },
        { label: 'Cancelled', value: 'CANCELLED' },
        { label: 'Cleared', value: 'CLEARED' },
        { label: 'Declined', value: 'DECLINED' },
        { label: 'Error', value: 'ERROR' },
        { label: 'New', value: 'NEW' },
        { label: 'Pending', value: 'PENDING' },
        { label: 'Processing', value: 'PROCESSING' },
        { label: 'Settled', value: 'SETTLED' },
        { label: 'Reversed', value: 'REVERSED' },
      ],
    },
  ];

  sortParams: DatatableSorting = { key: 'createdAt', sortProp: 'createdAt', sortDir: 'desc' };

  get activePage(): number {
    const routePage = this.activatedRoute?.snapshot?.queryParams?.page;
    return routePage ? parseInt(routePage, 10) : 0;
  }

  journalActivity$ = this.store.select(financialAccountFeature.selectJournalActivity);

  totalElements$ = this.store.select(financialAccountFeature.selectJournalActivityTotalElements);

  isLoading$ = this.store.select(financialAccountFeature.selectJournalActivityLoading);

  activeFilters$ = this.store.select(financialAccountFeature.selectJournalActivityActiveFilters);

  activeFiltersCount$ = this.store.select(financialAccountFeature.selectJournalActivityActiveFiltersCount);

  updateQueryParams(page?: RequestPageParams['page']): void {
    if (!isUndefined(page) && this.activePage !== page) {
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: { page: page > 0 ? page : undefined },
        queryParamsHandling: 'merge',
      });
    }
  }

  getJournalActivity({ page, size, sortParams }: FetchData): void {
    if (sortParams) {
      this.sortParams = sortParams;
    }

    this.updateQueryParams(page);

    this.store.dispatch(
      FinancialAcccountActions.loadJournalActivity({
        params: {
          page: page ?? 0,
          size,
          sortParams: sortParams ?? this.sortParams,
        },
      }),
    );
  }

  applyFilters(filterValues: FilterValues): void {
    this.store.dispatch(FinancialAcccountActions.setJournalActivityActiveFilters({ filters: { ...pickBy(filterValues, identity) } }));
    this.getJournalActivity({ page: 0 });
  }

  onActivityRowClick(transactionId?: string): void {
    if (!transactionId) return;

    this.router.navigateByUrl(`/app/transactions/${transactionId}/details`);
  }
}
